import i18n from '../../../i18n'
import { Component } from 'react'
import styled from 'styled-components'
import { FiChevronDown, FiSearch } from 'react-icons/fi'

const selectPlaceholder = () => i18n.t('select', { ns: 'common' }) + '...'
const searchPlaceholder = () => i18n.t('search', { ns: 'common' }) + '...'

const SelectContainer = styled.div`
  position: relative;
  width: 100%;
`

const SelectButton = styled.button`
  width: 100%;
  height: ${props => props.theme.controlHeight};
  padding: 0 2.5rem 0 0.875rem;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.radius.input};
  font-size: 0.875rem;
  font-family: ${props => props.theme.typography.fontFamily};
  background: ${props => props.theme.colors.background.secondary};
  color: ${props => props.hasValue ? props.theme.colors.text.primary : props.theme.colors.text.muted};
  transition: ${props => props.theme.transitions.default};
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    border-color: ${props => props.theme.colors.text.tertiary};
    background: ${props => props.theme.colors.background.hover};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.text.primary};
    background: ${props => props.theme.colors.background.primary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const IconWrapper = styled.div`
  position: absolute;
  right: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  gap: 0.5rem;
  align-items: center;
  pointer-events: none;

  svg {
    transition: transform 0.2s ease;
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`

const Dropdown = styled.div`
  position: absolute;
  top: ${props => props.openUpward ? 'auto' : 'calc(100% + 0.25rem)'};
  bottom: ${props => props.openUpward ? 'calc(100% + 0.25rem)' : 'auto'};
  left: 0;
  right: 0;
  background: ${props => props.theme.colors.background.primary};
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.radius.input};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 300px;
  display: flex;
  flex-direction: column;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(0)' : props.openUpward ? 'translateY(10px)' : 'translateY(-10px)'};
  transition: all 0.2s ease;
`

const SearchInputWrapper = styled.div`
  position: relative;
  padding: 0.5rem;
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
`

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem 0.5rem 2.25rem;
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.radius.input};
  font-size: 0.875rem;
  font-family: ${props => props.theme.typography.fontFamily};
  background: ${props => props.theme.colors.background.secondary};
  color: ${props => props.theme.colors.text.primary};
  transition: ${props => props.theme.transitions.default};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.text.primary};
    background: ${props => props.theme.colors.background.primary};
  }

  &::placeholder {
    color: ${props => props.theme.colors.text.muted};
  }
`

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 1.25rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.text.muted};
  pointer-events: none;
`

const OptionsList = styled.div`
  overflow-y: auto;
  max-height: 240px;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.background.secondary};
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border.medium};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.colors.text.tertiary};
  }
`

const Option = styled.div`
  padding: 0.625rem 0.875rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: ${props => props.isSelected ? props.theme.colors.text.primary : props.theme.colors.text.secondary};
  background: ${props => props.isSelected ? props.theme.colors.background.hover : 'transparent'};
  font-weight: ${props => props.isSelected ? '500' : '400'};
  transition: ${props => props.theme.transitions.default};
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: ${props => props.theme.colors.background.hover};
    color: ${props => props.theme.colors.text.primary};
  }

  &:active {
    background: ${props => props.theme.colors.background.secondary};
  }
`

const MultiCheck = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1.5px solid ${props => props.isSelected ? props.theme.colors.text.primary : props.theme.colors.border.medium};
  background: ${props => props.isSelected ? props.theme.colors.text.primary : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 10px;
  color: ${props => props.theme.colors.background.primary};
  transition: ${props => props.theme.transitions.default};
`

const MultiChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  flex: 1;
  min-width: 0;
`

const MultiChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: ${props => props.theme.colors.background.hover};
  border: 1px solid ${props => props.theme.colors.border.medium};
  color: ${props => props.theme.colors.text.primary};
  padding: 1px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const NoResults = styled.div`
  padding: 1.5rem 0.875rem;
  text-align: center;
  color: ${props => props.theme.colors.text.muted};
  font-size: 0.875rem;
`

/**
 * SearchableSelect - Select con barra di ricerca per filtrare le opzioni
 *
 * @example
 * <SearchableSelect
 *   value={this.state.cliente}
 *   onChange={(value) => this.setState({ cliente: value })}
 *   options={clientiOptions}
 *   placeholder="Seleziona cliente..."
 *   clearable
 * />
 *
 * @param {string} value - Valore selezionato
 * @param {function} onChange - Callback quando cambia la selezione: (value) => void
 * @param {Array} options - Array di opzioni: [{ value: 'id', label: 'Nome' }, ...]
 * @param {string} placeholder - Testo placeholder
 * @param {boolean} clearable - Se true, mostra pulsante X per deselezionare
 * @param {boolean} disabled - Se true, il select è disabilitato
 */
export default class SearchableSelect extends Component {
  constructor (props) {
    super(props)
    this.state = {
      isOpen: false,
      searchTerm: '',
      openUpward: false
    }
    this.containerRef = null
  }

  componentDidMount () {
    document.addEventListener('mousedown', this.handleClickOutside)
  }

  componentWillUnmount () {
    document.removeEventListener('mousedown', this.handleClickOutside)
  }

  handleClickOutside = (event) => {
    if (this.containerRef && !this.containerRef.contains(event.target)) {
      this.setState({ isOpen: false, searchTerm: '' })
    }
  }

  toggleDropdown = () => {
    if (this.props.disabled) return

    // Calcola se aprire verso l'alto o verso il basso
    if (!this.state.isOpen && this.containerRef) {
      const rect = this.containerRef.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top
      const dropdownHeight = 300 // max-height del dropdown

      // Apri verso l'alto se non c'è spazio sotto e c'è più spazio sopra
      const shouldOpenUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow

      this.setState(prev => ({
        isOpen: !prev.isOpen,
        searchTerm: '',
        openUpward: shouldOpenUpward
      }))
    } else {
      this.setState(prev => ({ isOpen: !prev.isOpen, searchTerm: '' }))
    }
  }

  handleOptionClick = (optionValue) => {
    const { onChange, multi, value } = this.props
    if (!onChange) return

    if (multi) {
      const current = Array.isArray(value) ? value : []
      const next = current.includes(optionValue)
        ? current.filter(v => v !== optionValue)
        : [...current, optionValue]
      onChange(next)
    } else {
      onChange(optionValue)
      this.setState({ isOpen: false, searchTerm: '' })
    }
  }

  handleSearchChange = (e) => {
    this.setState({ searchTerm: e.target.value })
  }

  getFilteredOptions = () => {
    const { options = [] } = this.props
    const { searchTerm } = this.state

    const sortedOptions = [...options].sort((a, b) => {
      const aIsEmpty = a.value === ''
      const bIsEmpty = b.value === ''
      if (aIsEmpty !== bIsEmpty) return aIsEmpty ? -1 : 1
      return String(a.label ?? '').localeCompare(String(b.label ?? ''), 'it', { sensitivity: 'base' })
    })

    // Se non c'è ricerca, ritorna tutte le opzioni ordinate
    if (!searchTerm) {
      return sortedOptions
    }

    // Filtra e mantieni l'ordine alfabetico
    const lowerSearch = searchTerm.toLowerCase()
    return sortedOptions.filter(option =>
      `${option.label ?? ''} ${option.searchText ?? ''}`.toLowerCase().includes(lowerSearch)
    )
  }

  getSelectedLabel = () => {
    const { value, options = [], placeholder = selectPlaceholder(), multi } = this.props

    if (multi) {
      const arr = Array.isArray(value) ? value : []
      if (arr.length === 0) return placeholder
      return null // handled in render with chips
    }

    if (!value) {
      return placeholder
    }

    const selectedOption = options.find(opt => opt.value === value)
    return selectedOption ? selectedOption.label : placeholder
  }

  getSelectedOptions = () => {
    const { value, options = [] } = this.props
    const arr = Array.isArray(value) ? value : []
    return options.filter(opt => arr.includes(opt.value))
  }

  render () {
    const { value, disabled, className, style, multi, placeholder = selectPlaceholder(), searchable = true } = this.props
    const { isOpen, searchTerm, openUpward } = this.state
    const filteredOptions = this.getFilteredOptions()
    const selectedLabel = this.getSelectedLabel()
    const multiSelected = multi ? this.getSelectedOptions() : []
    const hasValue = multi ? multiSelected.length > 0 : !!value

    return (
      <SelectContainer
        ref={ref => { this.containerRef = ref }}
        className={className}
        style={style}
      >
        <SelectButton
          type='button'
          onClick={this.toggleDropdown} // eslint-disable-line react/jsx-handler-names
          hasValue={hasValue}
          disabled={disabled}
        >
          {multi
            ? (
                multiSelected.length > 0
                  ? (
                    <MultiChips>
                      {multiSelected.map(opt => (
                        <MultiChip key={opt.value}>{opt.label}</MultiChip>
                      ))}
                    </MultiChips>
                    )
                  : placeholder
              )
            : selectedLabel}
          <IconWrapper isOpen={isOpen}>
            <FiChevronDown size={16} />
          </IconWrapper>
        </SelectButton>

        <Dropdown isOpen={isOpen} openUpward={openUpward}>
          {searchable && (
            <SearchInputWrapper>
              <SearchIcon size={16} />
              <SearchInput
                type='text'
                value={searchTerm}
                onChange={this.handleSearchChange}
                placeholder={searchPlaceholder()}
                onClick={(e) => e.stopPropagation()}
              />
            </SearchInputWrapper>
          )}

          <OptionsList>
            {filteredOptions.length > 0
              ? filteredOptions.map(option => {
                const isSelected = multi
                  ? (Array.isArray(value) && value.includes(option.value))
                  : option.value === value
                return (
                  <Option key={option.value} isSelected={isSelected} onClick={() => this.handleOptionClick(option.value)}>
                    {multi && <MultiCheck isSelected={isSelected}>{isSelected ? '✓' : ''}</MultiCheck>}
                    {option.label}
                  </Option>
                )
              })
              : <NoResults>{i18n.t('ui.nessunRisultato', { ns: 'common' })}</NoResults>}
          </OptionsList>
        </Dropdown>
      </SelectContainer>
    )
  }
}
